import { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import Header from '@/components/layout/Header';
import { formatDate, formatCurrency } from '@/lib/utils';
import { showSuccess } from '@/lib/swal';
import { CheckCircle, Calendar, MapPin, Mail, UserPlus } from 'lucide-react';
import { RSVP, Event } from '@/types';

export default function RSVPConfirmationPage() {
  const { rsvpId } = useParams<{ rsvpId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { unitId, eventId } = (location.state as { unitId?: string; eventId?: string }) || {};
  const [loading, setLoading] = useState(true);
  const [rsvp, setRsvp] = useState<RSVP | null>(null);
  const [event, setEvent] = useState<Event | null>(null);

  useEffect(() => {
    const fetchRsvpData = async () => {
      // Redirect to RSVP dashboard if we have all required parameters
      if (rsvpId && unitId && eventId) {
        // Fetch RSVP data to get userId for invite links
        try {
          const rsvpDoc = await getDoc(doc(db, 'units', unitId, 'events', eventId, 'rsvps', rsvpId));
          if (rsvpDoc.exists()) {
            setRsvp({
              id: rsvpDoc.id,
              ...rsvpDoc.data(),
              createdAt: rsvpDoc.data().createdAt?.toDate(),
              updatedAt: rsvpDoc.data().updatedAt?.toDate(),
            } as RSVP);
          }
        } catch (error) {
          console.error('Error fetching RSVP:', error);
        }
        navigate(`/unit/${unitId}/event/${eventId}/rsvp/${rsvpId}/dashboard`, { replace: true });
      } else {
        setLoading(false);
      }
    };
    
    fetchRsvpData();
  }, [rsvpId, unitId, eventId, navigate]);

  if (loading) {
    return (
      <div className="flex min-h-screen flex-col">
        <Header />
        <div className="flex flex-1 items-center justify-center">
          <div className="text-center">
            <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto" />
            <p className="text-muted-foreground">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="flex-1 px-4 py-8">
        <div className="container mx-auto max-w-2xl">
          <Card>
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
                <CheckCircle className="h-10 w-10 text-green-600" />
              </div>
              <CardTitle className="text-2xl">RSVP Confirmed!</CardTitle>
              <CardDescription>
                Your registration has been successfully submitted
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="rounded-lg border p-4">
                <h3 className="font-semibold mb-3">What's Next?</h3>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <Mail className="mr-2 h-5 w-5 text-muted-foreground mt-0.5" />
                    <span className="text-sm">
                      Check your email for a confirmation with event details
                    </span>
                  </li>
                  <li className="flex items-start">
                    <Calendar className="mr-2 h-5 w-5 text-muted-foreground mt-0.5" />
                    <span className="text-sm">
                      You can edit your RSVP or invite guests until the deadline
                    </span>
                  </li>
                  <li className="flex items-start">
                    <UserPlus className="mr-2 h-5 w-5 text-muted-foreground mt-0.5" />
                    <span className="text-sm">
                      Payment can be made now or closer to the event date
                    </span>
                  </li>
                </ul>
              </div>

              <div className="space-y-3">
                <Button className="w-full" onClick={() => navigate('/dashboard')}>
                  Go to Dashboard
                </Button>
                <Button 
                  className="w-full"
                  onClick={() => navigate(`/payment/INVOICE_ID`)}
                >
                  Make Payment
                </Button>
                {unitId && eventId && (
                  <>
                    <Button 
                      variant="outline" 
                      className="w-full"
                      onClick={() => navigate(`/unit/${unitId}/event/${eventId}/rsvp/${rsvpId}/edit`)}
                    >
                      Edit RSVP
                    </Button>
                    <Button 
                      variant="outline" 
                      className="w-full"
                      onClick={() => {
                        let inviteLink = `${window.location.origin}/unit/${unitId}/event/${eventId}/rsvp?invitedBy=${rsvp?.userId || rsvpId}`;
                        // Include ticket filter if present
                        if (rsvp?.ticketFilter) {
                          inviteLink += `&tickets=${rsvp.ticketFilter}`;
                        }
                        navigator.clipboard.writeText(inviteLink);
                        showSuccess('Invitation link copied!');
                      }}
                    >
                      Invite a Guest
                    </Button>
                  </>
                )}
              </div>

              <p className="text-center text-xs text-muted-foreground">
                Reference Number: {rsvpId?.slice(0, 8)}
              </p>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}

